/**
 * Scroll To Top Component
 *
 * Automatically scrolls to top on route change and provides
 * a manual scroll-to-top button for long pages.
 *
 * @version 1.0.0
 */

import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * Scroll restoration component
 *
 * Automatically scrolls to the top of the page when the route changes.
 * Should be placed at the top level of your app, inside BrowserRouter.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <BrowserRouter>
 *       <ScrollRestoration />
 *       <Routes>...</Routes>
 *     </BrowserRouter>
 *   );
 * }
 * ```
 */
export function ScrollRestoration(): null {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }, [pathname]);

  return null;
}

export interface ScrollToTopButtonProps {
  /** Threshold in pixels before button appears */
  threshold?: number;
  /** Whether to use smooth scrolling */
  smooth?: boolean;
  /** Button position */
  position?: 'bottom-right' | 'bottom-left' | 'bottom-center';
  /** Custom class name */
  className?: string;
  /** Custom aria-label */
  ariaLabel?: string;
  /** Z-index for the button */
  zIndex?: number;
}

/**
 * Scroll to top button component
 *
 * Shows a floating button when user scrolls down past a threshold.
 * Clicking the button smoothly scrolls back to the top.
 *
 * @example
 * ```tsx
 * function Layout() {
 *   return (
 *     <>
 *       <Header />
 *       <main>{children}</main>
 *       <Footer />
 *       <ScrollToTopButton threshold={400} />
 *     </>
 *   );
 * }
 * ```
 */
export function ScrollToTopButton({
  threshold = 400,
  smooth = true,
  position = 'bottom-right',
  className,
  ariaLabel = 'Scroll to top',
  zIndex = 40,
}: ScrollToTopButtonProps): JSX.Element {
  const [isVisible, setIsVisible] = useState(false);

  // Check scroll position and update visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsVisible(scrollY > threshold);
    };

    // Check initial scroll position
    handleScroll();

    // Add scroll listener with passive option for performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [threshold]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: smooth ? 'smooth' : 'instant',
    });
  }, [smooth]);

  // Handle keyboard interaction
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        scrollToTop();
      }
    },
    [scrollToTop]
  );

  // Position styles
  const positionStyles = {
    'bottom-right': 'right-4 sm:right-6',
    'bottom-left': 'left-4 sm:left-6',
    'bottom-center': 'left-1/2 -translate-x-1/2',
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          type="button"
          onClick={scrollToTop}
          onKeyDown={handleKeyDown}
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={cn(
            'fixed bottom-4 sm:bottom-6',
            positionStyles[position],
            'p-3 rounded-full',
            'bg-primary-blue text-white',
            'shadow-lg hover:shadow-xl',
            'hover:bg-primary-blue-dark',
            'focus:outline-none focus:ring-2 focus:ring-primary-blue focus:ring-offset-2',
            'transition-colors duration-200',
            'print:hidden',
            className
          )}
          style={{ zIndex }}
          aria-label={ariaLabel}
        >
          <ArrowUp className="w-5 h-5" aria-hidden="true" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

/**
 * Hook for programmatic scroll control
 *
 * @returns Object with scroll control functions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { scrollToTop, scrollToElement, scrollToPosition } = useScrollControl();
 *
 *   return (
 *     <button onClick={() => scrollToElement('my-section')}>
 *       Go to section
 *     </button>
 *   );
 * }
 * ```
 */
export function useScrollControl() {
  const scrollToTop = useCallback((smooth = true) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: smooth ? 'smooth' : 'instant',
    });
  }, []);

  const scrollToElement = useCallback((elementId: string, smooth = true, offset = 0) => {
    const element = document.getElementById(elementId);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top,
        left: 0,
        behavior: smooth ? 'smooth' : 'instant',
      });
    }
  }, []);

  const scrollToPosition = useCallback((position: number, smooth = true) => {
    window.scrollTo({
      top: position,
      left: 0,
      behavior: smooth ? 'smooth' : 'instant',
    });
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      left: 0,
      behavior: smooth ? 'smooth' : 'instant',
    });
  }, []);

  return {
    scrollToTop,
    scrollToElement,
    scrollToPosition,
    scrollToBottom,
  };
}

/**
 * Hook to get current scroll position
 *
 * @returns Current scroll Y position
 */
export function useScrollPosition(): number {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return scrollY;
}

export default ScrollToTopButton;
